import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useSearchParams, Outlet, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon } from 'lucide-react';
import { Hero } from './components/Hero';
import { HowItWorks } from './components/HowItWorks';
import { PopularServices } from './components/PopularServices';
import { StatsBar } from './components/StatsBar';
import { CTABanner } from './components/CTABanner';
import { Footer } from './components/Footer';
import { QuoteForm } from './components/QuoteForm';
import { ServicePicker } from './components/ServicePicker';
import { BrandedLandingPage } from './components/BrandedLandingPage';
import { StatesDirectory } from './components/StatesDirectory';
import { CostGuides } from './components/CostGuides';
import { NearMeService } from './components/NearMeService';
import { DaBellaLandingPage } from './components/DaBellaLandingPage';
import { AllAmericanGutterLandingPage } from './components/AllAmericanGutterLandingPage';
import { BathsRUsLandingPage } from './components/BathsRUsLandingPage';
import { FCILandingPage } from './components/FCILandingPage';
import { KDesignersLandingPage } from './components/KDesignersLandingPage';
import { KohlerLandingPage } from './components/KohlerLandingPage';
import { AIAdvisor } from './components/AIAdvisor';
import { ServiceComparePage } from './components/ServiceComparePage';
import { Advertorial } from './components/Advertorial';
const SERVICE_SLUGS = ['walk-in-showers', 'walk-in-tubs', 'stairlifts', 'window-replacement', 'gutter-guards', 'roofing', 'siding', 'solar', 'bathroom-remodel', 'kitchen-remodel', 'flooring', 'hvac', 'painting', 'plumbing', 'electrical', 'fencing', 'insulation', 'doors', 'garage-doors', 'home-security', 'pest-control', 'landscaping', 'cleaning', 'general'];
// ─── Helper hook: navigate + scroll to top ───
function useAppNavigate() {
  const navigate = useNavigate();
  return (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
}
// ─── Shared Nav component ───
function Nav({
  isScrolled,
  onNavigate,
  mobileMenuOpen,
  setMobileMenuOpen
}: {
  isScrolled: boolean;
  onNavigate: (path: string) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}) {
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-md py-4' : 'bg-slate-900 py-6'}`} data-id="element-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1">
        <div className="flex items-center justify-between" data-id="element-2">
          <a href="/" onClick={e => {
          e.preventDefault();
          onNavigate('/');
        }} className="text-2xl font-extrabold text-white tracking-tight focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-sm" data-id="element-3">
            FindService<span className="text-amber-500" data-id="element-4">Quotes</span>
          </a>

          <div className="hidden md:flex items-center gap-8" data-id="element-5">
            <button onClick={() => onNavigate('/quote-form')} className="h-10 px-5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-6">
              Get Free Quote
            </button>
          </div>

          <button className="md:hidden p-2 text-slate-200 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-7">
            {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-8" /> : <MenuIcon className="w-6 h-6" data-id="element-9" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-t border-slate-800 shadow-xl" data-id="element-10">
          <div className="px-4 pt-2 pb-6 space-y-1 flex flex-col" data-id="element-11">
            <div className="pt-2 px-3" data-id="element-12">
              <button onClick={() => {
            onNavigate('/quote-form');
            setMobileMenuOpen(false);
          }} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors" data-id="element-13">
                Get Free Quotes
              </button>
            </div>
          </div>
        </div>}
    </nav>;
}
// ─── Layout: Landing page (dynamic scroll, no pt-20, includes AIAdvisor) ───
function LandingLayout() {
  const navigateTo = useAppNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleSearch = (service: string, zip: string) => {
    if (service && SERVICE_SLUGS.includes(service)) {
      navigateTo(`/${service}/quote-form${zip ? `?zip=${zip}` : ''}`);
    } else {
      navigateTo('/quote-form');
    }
  };
  const handleServiceClick = (serviceSlug: string) => {
    navigateTo(`/near-me/${serviceSlug}`);
  };
  return <div className="min-h-screen bg-white font-sans selection:bg-amber-500 selection:text-white" data-id="element-14">
      <Nav isScrolled={isScrolled} onNavigate={navigateTo} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} data-id="element-15" />
      <main data-id="element-16">
        <Hero onSearch={handleSearch} data-id="element-17" />
        <HowItWorks data-id="element-18" />
        <PopularServices onServiceClick={handleServiceClick} data-id="element-19" />
        <StatsBar data-id="element-20" />

        <CTABanner data-id="element-21" />
      </main>
      <Footer onNavigate={navigateTo} data-id="element-22" />
      <AIAdvisor onNavigateToQuote={slug => {
      if (slug) {
        navigateTo(`/${slug}/quote-form`);
      } else {
        navigateTo('/quote-form');
      }
    }} data-id="element-23" />
    </div>;
}
// ─── Layout: Sub-pages with Nav (always scrolled) + pt-20 + Footer ───
function SubPageLayout() {
  const navigateTo = useAppNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  return <>
      <Nav isScrolled={true} onNavigate={navigateTo} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} data-id="element-24" />
      <div className="pt-20" data-id="element-25">
        <Outlet data-id="element-26" />
      </div>
      <Footer onNavigate={navigateTo} data-id="element-27" />
    </>;
}
// ─── Route wrappers for pages needing URL params ───
function ServiceQuoteFormRoute() {
  const {
    slug
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  return <QuoteForm initialService={slug || ''} initialZip={searchParams.get('zip') || ''} onClose={() => navigate('/')} data-id="element-28" />;
}
function GenericQuoteFormRoute() {
  const navigateTo = useAppNavigate();
  return <ServicePicker onSelectService={(slug, zip) => {
    navigateTo(`/${slug}/quote-form${zip ? `?zip=${zip}` : ''}`);
  }} onClose={() => navigateTo('/')} data-id="element-29" />;
}
function ChampionRoute() {
  const {
    service
  } = useParams();
  const navigate = useNavigate();
  return <BrandedLandingPage initialService={service || ''} onClose={() => navigate('/')} data-id="element-30" />;
}
function DaBellaRoute() {
  const {
    service
  } = useParams();
  const navigate = useNavigate();
  return <DaBellaLandingPage initialService={service || ''} onClose={() => navigate('/')} data-id="element-31" />;
}
function AllAmericanGutterRoute() {
  const {
    project
  } = useParams();
  const navigate = useNavigate();
  return <AllAmericanGutterLandingPage initialProject={project || ''} onClose={() => navigate('/')} data-id="element-32" />;
}
function BathsRUsRoute() {
  const {
    service
  } = useParams();
  const navigate = useNavigate();
  return <BathsRUsLandingPage initialService={service || ''} onClose={() => navigate('/')} data-id="element-33" />;
}
function FCIRoute() {
  const {
    service
  } = useParams();
  const navigate = useNavigate();
  return <FCILandingPage initialService={service || ''} onClose={() => navigate('/')} data-id="element-34" />;
}
function KDesignersRoute() {
  const {
    service
  } = useParams();
  const navigate = useNavigate();
  return <KDesignersLandingPage initialService={service || ''} onClose={() => navigate('/')} data-id="element-35" />;
}
function KohlerRoute() {
  const {
    product
  } = useParams();
  const navigate = useNavigate();
  return <KohlerLandingPage initialProduct={product || ''} onClose={() => navigate('/')} data-id="element-36" />;
}
function AdvertorialRoute() {
  const {
    slug,
    theme
  } = useParams();
  const navigateTo = useAppNavigate();
  return <Advertorial serviceSlug={slug || ''} articleTheme={theme || ''} onNavigate={navigateTo} data-id="element-37" />;
}
function ServiceCompareRoute() {
  const {
    slug
  } = useParams();
  const navigateTo = useAppNavigate();
  return <ServiceComparePage serviceSlug={slug || ''} onNavigate={navigateTo} data-id="element-38" />;
}
function StatesListRoute() {
  const navigateTo = useAppNavigate();
  return <StatesDirectory mode="list" onNavigate={navigateTo} data-id="element-39" />;
}
function StateDetailRoute() {
  const {
    stateCode
  } = useParams();
  const navigateTo = useAppNavigate();
  return <StatesDirectory mode="detail" stateCode={stateCode || ''} onNavigate={navigateTo} data-id="element-40" />;
}
function CostsListRoute() {
  const navigateTo = useAppNavigate();
  const handleSearch = (service: string, zip: string) => {
    if (service && SERVICE_SLUGS.includes(service)) {
      navigateTo(`/${service}/quote-form${zip ? `?zip=${zip}` : ''}`);
    } else {
      navigateTo('/quote-form');
    }
  };
  return <CostGuides mode="list" onNavigate={navigateTo} onSearch={handleSearch} data-id="element-41" />;
}
function CostDetailRoute() {
  const {
    serviceSlug
  } = useParams();
  const navigateTo = useAppNavigate();
  return <CostGuides mode="detail" serviceSlug={serviceSlug || ''} onNavigate={navigateTo} data-id="element-42" />;
}
function NearMeRoute() {
  const {
    serviceSlug
  } = useParams();
  const navigateTo = useAppNavigate();
  return <NearMeService serviceSlug={serviceSlug || ''} onNavigate={navigateTo} data-id="element-43" />;
}
// ─── Scroll to top on route change ───
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
// ─── Main App with Routes ───
export function App() {
  return <>
      <ScrollToTop data-id="element-44" />
      <Routes data-id="element-45">
        {/* Landing page — own layout with dynamic scroll + AIAdvisor */}
        <Route path="/" element={<LandingLayout data-id="element-47" />} data-id="element-46" />

        {/* Full-page takeover routes (no shared nav) */}
        <Route path="/quote-form" element={<GenericQuoteFormRoute data-id="element-49" />} data-id="element-48" />
        <Route path="/:slug/quote-form" element={<ServiceQuoteFormRoute data-id="element-51" />} data-id="element-50" />
        <Route path="/champion" element={<ChampionRoute data-id="element-53" />} data-id="element-52" />
        <Route path="/champion/:service" element={<ChampionRoute data-id="element-55" />} data-id="element-54" />
        <Route path="/dabella" element={<DaBellaRoute data-id="element-57" />} data-id="element-56" />
        <Route path="/dabella/:service" element={<DaBellaRoute data-id="element-59" />} data-id="element-58" />
        <Route path="/all-american-gutter" element={<AllAmericanGutterRoute data-id="element-61" />} data-id="element-60" />
        <Route path="/all-american-gutter/:project" element={<AllAmericanGutterRoute data-id="element-63" />} data-id="element-62" />
        <Route path="/baths-r-us" element={<BathsRUsRoute data-id="element-65" />} data-id="element-64" />
        <Route path="/baths-r-us/:service" element={<BathsRUsRoute data-id="element-67" />} data-id="element-66" />
        <Route path="/fci" element={<FCIRoute data-id="element-69" />} data-id="element-68" />
        <Route path="/fci/:service" element={<FCIRoute data-id="element-71" />} data-id="element-70" />
        <Route path="/k-designers" element={<KDesignersRoute data-id="element-73" />} data-id="element-72" />
        <Route path="/k-designers/:service" element={<KDesignersRoute data-id="element-75" />} data-id="element-74" />
        <Route path="/kohler" element={<KohlerRoute data-id="element-77" />} data-id="element-76" />
        <Route path="/kohler/:product" element={<KohlerRoute data-id="element-79" />} data-id="element-78" />
        <Route path="/:slug/articles/advertorial" element={<AdvertorialRoute data-id="element-81" />} data-id="element-80" />
        <Route path="/:slug/articles/advertorial/:theme" element={<AdvertorialRoute data-id="element-83" />} data-id="element-82" />

        {/* Sub-pages with shared Nav + Footer layout */}
        <Route element={<SubPageLayout data-id="element-85" />} data-id="element-84">
          <Route path="/:slug/best" element={<ServiceCompareRoute data-id="element-87" />} data-id="element-86" />
          <Route path="/states" element={<StatesListRoute data-id="element-89" />} data-id="element-88" />
          <Route path="/states/:stateCode" element={<StateDetailRoute data-id="element-91" />} data-id="element-90" />
          <Route path="/costs" element={<CostsListRoute data-id="element-93" />} data-id="element-92" />
          <Route path="/costs/:serviceSlug" element={<CostDetailRoute data-id="element-95" />} data-id="element-94" />
          <Route path="/near-me/:serviceSlug" element={<NearMeRoute data-id="element-97" />} data-id="element-96" />
        </Route>
      </Routes>
    </>;
}